// `BucketGrid` (design-system §3.14, D-354 ruling 7): one row per product, one
// column per weekly bucket, a quantity in each cell. The forecast editor and the
// MPS board share it. Every word, every value and every write come from the
// caller; the grid holds the keyboard, the cell states, the editor and the
// phone layout.

import { useEffect, useId, useRef, useState } from "react";
import type { ClipboardEvent, KeyboardEvent, ReactNode } from "react";
import { Button } from "./button";
import { Skeleton } from "./states";
import { useDesktop } from "./use-desktop";
import { editGridQty, formatGridQty, parseGridQty, sameQty } from "./bucket-grid-paste";

export type BucketCellState = "plain" | "overridden" | "frozen" | "dropped";

export interface BucketGridWeek {
  /** The bucket's Monday, `YYYY-MM-DD`. */
  readonly start: string;
  readonly label: string;
  /** A week shown that may not change, such as one before the current week. */
  readonly readOnly?: boolean;
}

export interface BucketGridCell {
  readonly value: string | null;
  readonly state?: BucketCellState;
  /** The popover text of an overridden or dropped cell: reason, author, time. */
  readonly stateNote?: string;
  /** Read-only with the lock icon whatever its state, as a frozen cell that also carries a dot. */
  readonly locked?: boolean;
  /** A change the screen holds and has not saved yet. */
  readonly edited?: boolean;
  /** A refusal the screen reports for this cell, in the caller's words. */
  readonly refusal?: string;
  readonly busy?: boolean;
}

export interface BucketGridSubRow {
  readonly key: string;
  readonly label: string;
  readonly values: Readonly<Record<string, string | null>>;
  /** A week's explanation of its value, shown as the sub-cell's tooltip and under a phone card. */
  readonly notes?: Readonly<Record<string, string>>;
}

export interface BucketGridRow {
  readonly key: string;
  readonly label: ReactNode;
  /** The row's name as plain text, for accessible names. */
  readonly labelText: string;
  readonly cells: Readonly<Record<string, BucketGridCell>>;
  readonly subRows?: readonly BucketGridSubRow[];
  readonly readOnly?: boolean;
}

export type BucketCommitAnswer =
  | { readonly status: "committed" }
  | { readonly status: "refused"; readonly message: string; readonly current?: string | null };

export interface BucketGridLabels {
  readonly grid: string;
  readonly product: string;
  readonly editCell: (row: string, week: string) => string;
  readonly quantity: string;
  readonly reason: string;
  readonly commit: string;
  readonly cancel: string;
  readonly showDetails: (row: string) => string;
  readonly hideDetails: (row: string) => string;
  readonly frozen: string;
  readonly overridden: string;
  readonly dropped: string;
  readonly edited: string;
  readonly busy: string;
  readonly invalid: (reason: "not_number" | "precision") => string;
  readonly previousWeek: string;
  readonly nextWeek: string;
  readonly phoneWeek: (week: string) => string;
  readonly empty: string;
}

export interface BucketGridProps {
  readonly rows: readonly BucketGridRow[];
  readonly weeks: readonly BucketGridWeek[];
  readonly locale: string;
  readonly labels: BucketGridLabels;
  /** Cells may be edited at desktop widths. A phone never edits. */
  readonly editable?: boolean;
  /** The editor asks for a reason, and commit waits for one. */
  readonly reasonRequired?: boolean;
  /** Values are changes, written with their sign. */
  readonly signed?: boolean;
  /** One cell's new value; a promise makes the cell busy until it answers. */
  readonly onCommit?: (
    rowKey: string,
    week: string,
    value: string | null,
    reason?: string,
  ) => Promise<BucketCommitAnswer> | undefined;
  /** A pasted block, anchored at the focused cell. */
  readonly onPaste?: (text: string, anchor: { rowIndex: number; weekIndex: number }) => void;
  readonly onFocusCell?: (rowKey: string, week: string) => void;
  readonly loading?: boolean;
  readonly loadingLabel?: string;
  readonly "data-testid"?: string;
}

interface Position {
  readonly r: number;
  readonly c: number;
}

interface CellOverride {
  readonly value: string | null;
  /** The value the screen held when the override began; a new one ends it. */
  readonly base: string | null;
}

const cellKey = (rowKey: string, week: string) => `${rowKey}|${week}`;

export function BucketGrid(props: BucketGridProps) {
  const desktop = useDesktop();
  if (props.loading === true) return <BucketGridSkeleton {...props} desktop={desktop} />;
  return desktop ? <DesktopGrid {...props} /> : <PhoneCards {...props} />;
}

function BucketGridSkeleton(props: BucketGridProps & { desktop: boolean }) {
  const testId = props["data-testid"] ?? "bucket-grid";
  if (!props.desktop) {
    return (
      <div
        className="ds-bg-phone"
        role="status"
        aria-label={props.loadingLabel}
        data-testid={`${testId}-loading`}
      >
        <Skeleton height="1.75rem" />
        <Skeleton height="4rem" />
        <Skeleton height="4rem" />
      </div>
    );
  }
  const weeks = props.weeks.length > 0 ? props.weeks.length : 8;
  return (
    <div
      className="ds-bg-wrap"
      role="status"
      aria-label={props.loadingLabel}
      data-testid={`${testId}-loading`}
    >
      <table className="ds-bg" aria-hidden="true">
        <thead>
          <tr>
            <th className="ds-bg-pin">
              <Skeleton width="60%" height="0.9rem" />
            </th>
            {Array.from({ length: weeks }, (_, i) => (
              <th key={i}>
                <Skeleton height="0.9rem" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 4 }, (_, r) => (
            <tr key={r}>
              <th className="ds-bg-pin">
                <Skeleton height="0.9rem" />
              </th>
              {Array.from({ length: weeks }, (_, c) => (
                <td key={c} className="ds-bg-cell" />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function useCellOverrides() {
  const [overrides, setOverrides] = useState<ReadonlyMap<string, CellOverride>>(new Map());
  const [refusals, setRefusals] = useState<ReadonlyMap<string, string>>(new Map());
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());

  // An override gives way once the screen's own value moves off its base.
  const shown = (row: BucketGridRow, week: string): string | null => {
    const held = row.cells[week]?.value ?? null;
    const o = overrides.get(cellKey(row.key, week));
    if (o === undefined) return held;
    return sameQty(held, o.base) ? o.value : held;
  };

  return { overrides, setOverrides, refusals, setRefusals, busy, setBusy, shown };
}

function DesktopGrid(props: BucketGridProps) {
  const { rows, weeks, labels, locale } = props;
  const testId = props["data-testid"] ?? "bucket-grid";
  const editable = props.editable === true && props.onCommit !== undefined;
  const [active, setActiveState] = useState<Position>({ r: 0, c: 0 });
  // The paste handler reads the focused cell from here, ahead of the next render.
  const activeRef = useRef<Position>({ r: 0, c: 0 });
  const setActive = (pos: Position) => {
    activeRef.current = pos;
    setActiveState(pos);
  };
  const [editing, setEditing] = useState<Position | undefined>(undefined);
  const [draft, setDraft] = useState("");
  const [reason, setReason] = useState("");
  const [invalid, setInvalid] = useState<"not_number" | "precision" | undefined>(undefined);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [openNote, setOpenNote] = useState<string | undefined>(undefined);
  const cells = useRef(new Map<string, HTMLTableCellElement>());
  const state = useCellOverrides();
  const editorId = useId();

  const rowCount = rows.length;
  const colCount = weeks.length;
  const safe: Position = {
    r: Math.min(active.r, Math.max(0, rowCount - 1)),
    c: Math.min(active.c, Math.max(0, colCount - 1)),
  };

  const focusCell = (pos: Position) => {
    setActive(pos);
    const row = rows[pos.r];
    const week = weeks[pos.c];
    if (row === undefined || week === undefined) return;
    cells.current.get(cellKey(row.key, week.start))?.focus();
    props.onFocusCell?.(row.key, week.start);
  };

  const canEdit = (row: BucketGridRow, week: BucketGridWeek): boolean =>
    editable &&
    row.readOnly !== true &&
    week.readOnly !== true &&
    row.cells[week.start]?.state !== "frozen" &&
    row.cells[week.start]?.locked !== true &&
    !state.busy.has(cellKey(row.key, week.start));

  const openEditor = (pos: Position) => {
    const row = rows[pos.r];
    const week = weeks[pos.c];
    if (row === undefined || week === undefined || !canEdit(row, week)) return;
    const value = state.shown(row, week.start);
    setDraft(value === null ? "" : editGridQty(value, locale));
    setReason("");
    setInvalid(undefined);
    setEditing(pos);
  };

  const closeEditor = (pos: Position, refocus: boolean) => {
    setEditing(undefined);
    setInvalid(undefined);
    // A key closed it, so focus returns to the cell once the editor unmounts.
    if (refocus) {
      requestAnimationFrame(() => {
        focusCell(pos);
      });
    }
  };

  const commit = (pos: Position, refocus: boolean) => {
    const row = rows[pos.r];
    const week = weeks[pos.c];
    if (row === undefined || week === undefined || props.onCommit === undefined) return;
    let value: string | null = null;
    if (draft.trim() !== "") {
      const parsed = parseGridQty(draft, locale);
      if (!parsed.ok) {
        setInvalid(parsed.reason);
        return;
      }
      value = parsed.value;
    }
    if (props.reasonRequired === true && reason.trim() === "") return;
    const key = cellKey(row.key, week.start);
    const before = state.shown(row, week.start);
    closeEditor(pos, refocus);
    if (sameQty(value, before)) return;
    state.setRefusals((m) => {
      const next = new Map(m);
      next.delete(key);
      return next;
    });
    const answer = props.onCommit(
      row.key,
      week.start,
      value,
      props.reasonRequired === true ? reason.trim() : undefined,
    );
    if (answer === undefined) return;
    const base = row.cells[week.start]?.value ?? null;
    state.setOverrides((m) => new Map(m).set(key, { value, base }));
    state.setBusy((s) => new Set(s).add(key));
    void answer.then(
      (result) => {
        settle(key, base, result);
      },
      () => {
        settle(key, base, { status: "refused", message: "" });
      },
    );
  };

  const settle = (key: string, base: string | null, result: BucketCommitAnswer) => {
    state.setBusy((s) => {
      const next = new Set(s);
      next.delete(key);
      return next;
    });
    if (result.status === "committed") return;
    state.setOverrides((m) => {
      const next = new Map(m);
      if (result.current !== undefined) next.set(key, { value: result.current, base });
      else next.delete(key);
      return next;
    });
    state.setRefusals((m) => new Map(m).set(key, result.message));
  };

  const onCellKey = (e: KeyboardEvent<HTMLTableCellElement>, pos: Position) => {
    const move = (r: number, c: number) => {
      e.preventDefault();
      focusCell({
        r: Math.max(0, Math.min(rowCount - 1, r)),
        c: Math.max(0, Math.min(colCount - 1, c)),
      });
    };
    switch (e.key) {
      case "ArrowRight":
        move(pos.r, pos.c + 1);
        break;
      case "ArrowLeft":
        move(pos.r, pos.c - 1);
        break;
      case "ArrowDown":
        move(pos.r + 1, pos.c);
        break;
      case "ArrowUp":
        move(pos.r - 1, pos.c);
        break;
      case "Home":
        move(pos.r, 0);
        break;
      case "End":
        move(pos.r, colCount - 1);
        break;
      case "Tab": {
        const forward = !e.shiftKey;
        const flat = pos.r * colCount + pos.c + (forward ? 1 : -1);
        // At either end of the grid Tab leaves it as usual.
        if (flat < 0 || flat >= rowCount * colCount) return;
        move(Math.floor(flat / colCount), flat % colCount);
        break;
      }
      case "Enter":
      case "F2":
        e.preventDefault();
        openEditor(pos);
        break;
      default:
        break;
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    if (!editable || props.onPaste === undefined || editing !== undefined) return;
    const text = e.clipboardData.getData("text/plain");
    if (text.length === 0) return;
    e.preventDefault();
    const at = activeRef.current;
    props.onPaste(text, {
      rowIndex: Math.min(at.r, Math.max(0, rowCount - 1)),
      weekIndex: Math.min(at.c, Math.max(0, colCount - 1)),
    });
  };

  useEffect(() => {
    if (openNote === undefined) return undefined;
    const close = () => {
      setOpenNote(undefined);
    };
    document.addEventListener("pointerdown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
    };
  }, [openNote]);

  return (
    <div className="ds-bg-wrap" onPaste={onPaste} data-testid={testId}>
      <table className="ds-bg" role="grid" aria-label={labels.grid}>
        <thead>
          <tr>
            <th scope="col" className="ds-bg-pin">
              {labels.product}
            </th>
            {weeks.map((week) => (
              <th
                key={week.start}
                scope="col"
                className={week.readOnly === true ? "ds-bg-wk is-past" : "ds-bg-wk"}
                data-week={week.start}
              >
                {week.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => {
            const open = expanded.has(row.key);
            const subRows = row.subRows ?? [];
            return [
              <tr key={row.key} data-testid={`${testId}-row`} data-row-key={row.key}>
                <th scope="row" className="ds-bg-pin">
                  <span className="ds-bg-label">{row.label}</span>
                  {subRows.length > 0 ? (
                    <button
                      type="button"
                      className="ds-bg-toggle"
                      aria-expanded={open}
                      aria-label={
                        open ? labels.hideDetails(row.labelText) : labels.showDetails(row.labelText)
                      }
                      onClick={() => {
                        setExpanded((s) => {
                          const next = new Set(s);
                          if (next.has(row.key)) next.delete(row.key);
                          else next.add(row.key);
                          return next;
                        });
                      }}
                    >
                      {open ? "−" : "+"}
                    </button>
                  ) : null}
                </th>
                {weeks.map((week, c) => {
                  const key = cellKey(row.key, week.start);
                  const cell = row.cells[week.start];
                  const value = state.shown(row, week.start);
                  const isBusy = state.busy.has(key) || cell?.busy === true;
                  const refusal = state.refusals.get(key) ?? cell?.refusal;
                  const cellState = cell?.state ?? "plain";
                  const locked = cellState === "frozen" || cell?.locked === true;
                  const isEditing = editing?.r === r && editing.c === c;
                  const isActive = safe.r === r && safe.c === c;
                  const writable = canEdit(row, week);
                  const classes = ["ds-bg-cell"];
                  if (cell?.edited === true) classes.push("is-edited");
                  if (refusal !== undefined) classes.push("is-refused");
                  if (cellState !== "plain") classes.push(`is-${cellState}`);
                  if (locked && cellState !== "frozen") classes.push("is-locked");
                  if (week.readOnly === true) classes.push("is-past");
                  const descriptions = [
                    locked ? labels.frozen : undefined,
                    cell?.edited === true ? labels.edited : undefined,
                    isBusy ? labels.busy : undefined,
                    refusal,
                  ].filter((d): d is string => d !== undefined && d.length > 0);
                  return (
                    <td
                      key={week.start}
                      ref={(el) => {
                        if (el === null) cells.current.delete(key);
                        else cells.current.set(key, el);
                      }}
                      role="gridcell"
                      className={classes.join(" ")}
                      tabIndex={isActive ? 0 : -1}
                      aria-readonly={!writable}
                      aria-busy={isBusy || undefined}
                      aria-label={labels.editCell(row.labelText, week.label)}
                      aria-describedby={
                        descriptions.length > 0 ? `${editorId}-d-${r}-${c}` : undefined
                      }
                      title={refusal}
                      data-testid={`${testId}-cell`}
                      data-row-key={row.key}
                      data-week={week.start}
                      data-state={cellState}
                      data-locked={locked || undefined}
                      data-value={value ?? undefined}
                      onFocus={() => {
                        if (!isActive) setActive({ r, c });
                      }}
                      onKeyDown={(e) => {
                        if (!isEditing) onCellKey(e, { r, c });
                      }}
                      onDoubleClick={() => {
                        openEditor({ r, c });
                      }}
                    >
                      <span className="ds-bg-num">
                        {formatGridQty(value, locale, props.signed === true)}
                      </span>
                      {descriptions.length > 0 ? (
                        <span id={`${editorId}-d-${r}-${c}`} hidden>
                          {descriptions.join(". ")}
                        </span>
                      ) : null}
                      {locked ? <LockMark /> : null}
                      {cellState === "overridden" || cellState === "dropped" ? (
                        <button
                          type="button"
                          tabIndex={-1}
                          className={[
                            "ds-bg-dot",
                            cellState === "overridden" ? "is-amber" : "is-red",
                            locked ? "is-beside-lock" : "",
                          ]
                            .join(" ")
                            .trim()}
                          aria-label={
                            cellState === "overridden" ? labels.overridden : labels.dropped
                          }
                          aria-expanded={openNote === key}
                          onPointerDown={(e) => {
                            e.stopPropagation();
                          }}
                          onClick={() => {
                            setOpenNote(openNote === key ? undefined : key);
                          }}
                        />
                      ) : null}
                      {openNote === key && cell?.stateNote !== undefined ? (
                        <span className="ds-bg-note" role="note">
                          {cell.stateNote}
                        </span>
                      ) : null}
                      {isEditing ? (
                        <CellEditorBox
                          id={editorId}
                          draft={draft}
                          reason={reason}
                          reasonRequired={props.reasonRequired === true}
                          invalid={invalid}
                          labels={labels}
                          onDraft={(v) => {
                            setDraft(v);
                            setInvalid(undefined);
                          }}
                          onReason={setReason}
                          onCommit={(refocus) => {
                            commit({ r, c }, refocus);
                          }}
                          onCancel={() => {
                            closeEditor({ r, c }, true);
                          }}
                        />
                      ) : null}
                    </td>
                  );
                })}
              </tr>,
              ...(open
                ? subRows.map((sub) => (
                    <tr key={`${row.key}:${sub.key}`} className="ds-bg-sub" data-sub-row={sub.key}>
                      <th scope="row" className="ds-bg-pin ds-bg-sublabel">
                        {sub.label}
                      </th>
                      {weeks.map((week) => {
                        const note = sub.notes?.[week.start];
                        return (
                          <td
                            key={week.start}
                            className={
                              note !== undefined
                                ? "ds-bg-cell is-sub has-note"
                                : "ds-bg-cell is-sub"
                            }
                            title={note}
                            data-testid={`${testId}-sub-cell`}
                            data-sub-row={sub.key}
                            data-parent-row={row.key}
                            data-week={week.start}
                            data-value={sub.values[week.start] ?? undefined}
                          >
                            <span className="ds-bg-num">
                              {formatGridQty(sub.values[week.start] ?? null, locale)}
                            </span>
                            {note !== undefined ? (
                              <span className="visually-hidden">{note}</span>
                            ) : null}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                : []),
            ];
          })}
        </tbody>
      </table>
      {rows.length === 0 ? <p className="ds-bg-empty">{labels.empty}</p> : null}
    </div>
  );
}

function CellEditorBox(props: {
  id: string;
  draft: string;
  reason: string;
  reasonRequired: boolean;
  invalid: "not_number" | "precision" | undefined;
  labels: BucketGridLabels;
  onDraft: (v: string) => void;
  onReason: (v: string) => void;
  onCommit: (refocus: boolean) => void;
  onCancel: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  // Set once a key or a button has decided, so the blur that follows does not decide again.
  const decided = useRef(false);
  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, []);
  const onKey = (e: KeyboardEvent<HTMLElement>) => {
    e.stopPropagation();
    if (e.key === "Escape") {
      e.preventDefault();
      decided.current = true;
      props.onCancel();
    } else if (e.key === "Enter" && !(e.target instanceof HTMLTextAreaElement)) {
      e.preventDefault();
      decided.current = true;
      props.onCommit(true);
    }
  };
  const errorId = `${props.id}-err`;
  const qty = (
    <input
      ref={input}
      className="ds-bg-input"
      inputMode="decimal"
      aria-label={props.labels.quantity}
      aria-invalid={props.invalid !== undefined || undefined}
      aria-describedby={props.invalid !== undefined ? errorId : undefined}
      value={props.draft}
      onChange={(e) => {
        decided.current = false;
        props.onDraft(e.target.value);
      }}
      onKeyDown={onKey}
      onBlur={(e) => {
        // Leaving a plain editor commits it; the reason popover keeps focus inside.
        if (decided.current || props.reasonRequired) return;
        if (!e.currentTarget.parentElement?.contains(e.relatedTarget)) props.onCommit(false);
      }}
      data-testid="bucket-grid-input"
    />
  );
  const error =
    props.invalid !== undefined ? (
      <span id={errorId} className="ds-bg-error" role="alert">
        {props.labels.invalid(props.invalid)}
      </span>
    ) : null;
  if (!props.reasonRequired) {
    return (
      <span className="ds-bg-edit">
        {qty}
        {error}
      </span>
    );
  }
  return (
    <span className="ds-bg-edit">
      {qty}
      <span className="ds-bg-pop" role="dialog" aria-label={props.labels.reason} onKeyDown={onKey}>
        {error}
        <textarea
          className="ds-bg-reason"
          aria-label={props.labels.reason}
          value={props.reason}
          rows={2}
          onChange={(e) => {
            props.onReason(e.target.value);
          }}
          data-testid="bucket-grid-reason"
        />
        <span className="ds-bg-pop-actions">
          <Button size="sm" onClick={props.onCancel}>
            {props.labels.cancel}
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={props.reason.trim() === ""}
            onClick={() => {
              props.onCommit(true);
            }}
          >
            {props.labels.commit}
          </Button>
        </span>
      </span>
    </span>
  );
}

function LockMark() {
  return (
    <svg className="ds-bg-lock" viewBox="0 0 12 12" aria-hidden="true">
      <rect x="2.5" y="5.5" width="7" height="5" rx="1" />
      <path d="M4 5.5V4a2 2 0 0 1 4 0v1.5" />
    </svg>
  );
}

function PhoneCards(props: BucketGridProps) {
  const { rows, weeks, labels, locale } = props;
  const testId = props["data-testid"] ?? "bucket-grid";
  const firstWritable = Math.max(
    0,
    weeks.findIndex((w) => w.readOnly !== true),
  );
  const [index, setIndex] = useState(firstWritable);
  const at = Math.min(index, Math.max(0, weeks.length - 1));
  const week = weeks[at];
  return (
    <div className="ds-bg-phone" data-testid={testId}>
      <div className="ds-bg-stepper">
        <Button
          size="sm"
          disabled={at <= 0}
          aria-label={labels.previousWeek}
          onClick={() => {
            setIndex(at - 1);
          }}
        >
          ‹
        </Button>
        <span className="ds-bg-stepper-wk" aria-live="polite" data-testid={`${testId}-phone-week`}>
          {week !== undefined ? labels.phoneWeek(week.label) : ""}
        </span>
        <Button
          size="sm"
          disabled={at >= weeks.length - 1}
          aria-label={labels.nextWeek}
          onClick={() => {
            setIndex(at + 1);
          }}
        >
          ›
        </Button>
      </div>
      {rows.length === 0 ? <p className="ds-bg-empty">{labels.empty}</p> : null}
      <ul className="ds-bg-cards">
        {rows.map((row) => {
          const cell = week !== undefined ? row.cells[week.start] : undefined;
          const cellState = cell?.state ?? "plain";
          const locked = cellState === "frozen" || cell?.locked === true;
          return (
            <li
              key={row.key}
              className="ds-bg-card"
              data-testid={`${testId}-card`}
              data-row-key={row.key}
            >
              <span className="ds-bg-card-label">{row.label}</span>
              <span
                className="ds-bg-card-qty"
                data-state={cellState}
                data-locked={locked || undefined}
              >
                <span className="ds-bg-num">
                  {formatGridQty(cell?.value ?? null, locale, props.signed === true)}
                </span>
                {locked ? <LockMark /> : null}
                {cellState === "overridden" ? (
                  <span className="ds-bg-dot is-amber" title={cell?.stateNote} />
                ) : null}
                {cellState === "dropped" ? (
                  <span className="ds-bg-dot is-red" title={cell?.stateNote} />
                ) : null}
              </span>
              {(row.subRows ?? []).map((sub) => {
                const note = week !== undefined ? sub.notes?.[week.start] : undefined;
                return (
                  <span key={sub.key} className="ds-bg-card-subgroup" data-sub-row={sub.key}>
                    <span className="ds-bg-card-sub">
                      <span>{sub.label}</span>
                      <span className="ds-bg-num">
                        {week !== undefined
                          ? formatGridQty(sub.values[week.start] ?? null, locale)
                          : ""}
                      </span>
                    </span>
                    {note !== undefined ? <span className="ds-bg-card-note">{note}</span> : null}
                  </span>
                );
              })}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
